/**
 * F7 — Folder export via the File System Access API.
 *
 * Walks a PortableSite + writes each note (with YAML frontmatter
 * restored), each asset (as binary), and each flow into the folder
 * picked by `showDirectoryPicker({mode:'readwrite'})`. Chrome / Edge
 * only — Safari/Firefox don't support readwrite mode yet; callers
 * fall back to `toZip` on those.
 *
 * Frontmatter is re-emitted by the SharePoint projection (which is
 * the canonical "outbound" form). We reuse that here so a folder
 * export and a SharePoint export carry the same bytes.
 */

import { toSharePointTree } from '../../portable-site';
import type { PortableSite } from '../../portable-site';

interface FileSystemFileHandleLike {
  createWritable: () => Promise<FileSystemWritableFileStreamLike>;
}
interface FileSystemWritableFileStreamLike {
  write: (data: Blob | ArrayBuffer | string) => Promise<void>;
  close: () => Promise<void>;
}
interface FileSystemDirectoryHandleLike {
  name: string;
  getDirectoryHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FileSystemDirectoryHandleLike>;
  getFileHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FileSystemFileHandleLike>;
}

export function isShowDirectoryPickerWritableAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as { showDirectoryPicker?: unknown };
  return typeof w.showDirectoryPicker === 'function';
}

export interface ExportToFolderOpts {
  /**
   * Custom directory handle. When omitted the function calls
   * `showDirectoryPicker({mode:'readwrite'})` and uses the returned
   * handle. Tests inject a fake handle directly.
   */
  directoryHandle?: FileSystemDirectoryHandleLike;
}

export interface ExportToFolderResult {
  filesWritten: number;
  assetsWritten: number;
  folderName: string;
}

export async function exportToFolder(
  site: PortableSite,
  opts: ExportToFolderOpts = {},
): Promise<ExportToFolderResult> {
  let dir = opts.directoryHandle;
  if (!dir) {
    if (!isShowDirectoryPickerWritableAvailable()) {
      throw new Error('showDirectoryPicker is not available in this browser. Use the ZIP export instead.');
    }
    const w = window as unknown as {
      showDirectoryPicker: (init?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandleLike>;
    };
    dir = await w.showDirectoryPicker({ mode: 'readwrite' });
  }
  const tree = toSharePointTree(site);
  let filesWritten = 0;
  let assetsWritten = 0;
  for (const f of tree.files) {
    const handle = await ensureFilePath(dir, f.path);
    const writable = await handle.createWritable();
    await writable.write(f.body);
    await writable.close();
    filesWritten++;
  }
  for (const a of tree.assets) {
    const handle = await ensureFilePath(dir, a.path);
    const writable = await handle.createWritable();
    // WritableFileStream.write accepts Blob | ArrayBuffer | string; a
    // raw Uint8Array view doesn't satisfy the ArrayBuffer type bound in
    // TS lib.dom, so we wrap in a Blob with the asset's MIME type.
    const blob = new Blob([a.bytes as BlobPart], { type: a.mimeType ?? 'application/octet-stream' });
    await writable.write(blob);
    await writable.close();
    assetsWritten++;
  }
  return { filesWritten, assetsWritten, folderName: dir.name };
}

async function ensureFilePath(
  root: FileSystemDirectoryHandleLike,
  relPath: string,
): Promise<FileSystemFileHandleLike> {
  const parts = relPath.split('/').filter(Boolean);
  if (parts.length === 0) throw new Error(`Empty export path`);
  const fileName = parts[parts.length - 1]!;
  let cur = root;
  for (let i = 0; i < parts.length - 1; i++) {
    cur = await cur.getDirectoryHandle(parts[i]!, { create: true });
  }
  return cur.getFileHandle(fileName, { create: true });
}
